import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { useMemo, useState } from "react";
import { useData } from "../../services/DataProvider";

const BackfillNormalizeQuizQuestionReferences: React.FC = () => {
  const { quizzes, questions, adminCreateRecord } = useData();
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const questionReferencePlan = useMemo(() => {
    const questionById = new Map<string, typeof questions[number]>();
    questions.forEach((question) => {
      if (question.id) {
        questionById.set(question.id, question);
      }
    });

    let missingQuestions = 0;
    let quizRefsToAdd = 0;
    let quizRefsAlreadyPresent = 0;
    let questionRefsToRemove = 0;
    let totalQuizToQuestionRefs = 0;
    let totalQuestionToQuizRefs = 0;
    let commonRefs = 0;

    const quizAdds = new Map<string, Set<string>>();
    const questionRemovals = new Set<string>();

    quizzes.forEach((quiz) => {
      totalQuizToQuestionRefs += quiz.questionIDs?.length || 0;
    });

    questions.forEach((question) => {
      const quizId = question.quizID;
      if (!quizId) {
        return;
      }
      totalQuestionToQuizRefs += 1;
      questionRefsToRemove += 1;
      const quiz = quizzes.find((entry) => entry.id === quizId);
      if (!quiz) {
        missingQuestions += 1;
        questionRemovals.add(question.id);
        return;
      }
      const alreadyLinked = quiz.questionIDs?.includes(question.id) ?? false;
      if (alreadyLinked) {
        quizRefsAlreadyPresent += 1;
        commonRefs += 1;
      } else {
        quizRefsToAdd += 1;
        const set = quizAdds.get(quizId) || new Set<string>();
        set.add(question.id);
        quizAdds.set(quizId, set);
      }
      questionRemovals.add(question.id);
    });

    return {
      quizAdds,
      questionRemovals,
      quizRefsToAdd,
      quizRefsAlreadyPresent,
      questionRefsToRemove,
      missingQuestions,
      totalQuizToQuestionRefs,
      totalQuestionToQuizRefs,
      commonRefs,
    };
  }, [quizzes, questions]);

  const runQuestionReferenceBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    try {
      await Promise.all(
        Array.from(questionReferencePlan.quizAdds.entries()).map(async ([quizId, questionIds]) => {
          const quiz = quizzes.find((entry) => entry.id === quizId);
          if (!quiz) {
            return;
          }
          const nextQuestionIds = Array.from(new Set([...(quiz.questionIDs || []), ...questionIds]));
          await adminCreateRecord("finlitri-quizzes", {
            ...quiz,
            questionIDs: nextQuestionIds,
          });
        })
      );

      await Promise.all(
        Array.from(questionReferencePlan.questionRemovals.values()).map(async (questionId) => {
          const question = questions.find((entry) => entry.id === questionId);
          if (!question) {
            return;
          }
          await adminCreateRecord("finlitri-questions", {
            ...question,
            quizID: "",
          });
        })
      );

      setResultMessage("Question reference normalization completed successfully.");
    } catch (error) {
      setResultMessage(`Question reference normalization failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Normalize Quiz Question References</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          This backfill moves question.quizID into quiz.questionIDs and then clears question.quizID,
          so quiz/question links are stored only on the quiz side.
        </p>
        <p><b>Quiz → Question refs (quiz.questionIDs): </b> {questionReferencePlan.totalQuizToQuestionRefs}</p>
        <p><b>Question → Quiz refs (question.quizID): </b> {questionReferencePlan.totalQuestionToQuizRefs}</p>
        <p><b>Refs in common (question points to quiz that points to it): </b> {questionReferencePlan.commonRefs}</p>
        <p><b>Quiz refs missing on quiz (will be added): </b> {questionReferencePlan.quizRefsToAdd}</p>
        <p><b>Already present on quiz (quiz.questionIDs includes question): </b> {questionReferencePlan.quizRefsAlreadyPresent}</p>
        <p><b>Question → Quiz refs to remove: </b> {questionReferencePlan.questionRefsToRemove}</p>
        <p><b>Missing quiz docs (question.quizID points to non-existent quiz): </b> {questionReferencePlan.missingQuestions}</p>

        <IonButton
          expand="block"
          onClick={runQuestionReferenceBackfill}
          disabled={running || questionReferencePlan.questionRefsToRemove === 0}
          aria-label="Normalize quiz question references"
          title="Normalize quiz question references"
        >
          {running ? "Running..." : "Run Normalization"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillNormalizeQuizQuestionReferences;
